import { ref } from 'vue';
import { useReverseIntegrationStore } from './reverse';
import { useTravelLineStore } from './travelLine';
import type { ArrayElementType } from '@/core/types/gengeric';
import { useLockResource } from '@/core/composables/useLockResource';
import { loadable } from '@/core/utils/store';
import {
  type AddTariffScheduleServiceGroupInput,
  type AddTariffScheduleServiceGroupMutation,
  type GetContractServiceGroupsQuery,
  type GetServiceGroupSuggestedServicesQuery,
  type GetTariffScheduleServiceGroupQuery,
  type TariffScheduleServiceGroupsQuery,
  type UpdateTariffScheduleServiceGroupInput,
  type UpdateTariffScheduleServiceGroupListInput,
  useAddTariffScheduleServiceGroupMutation,
  useCalculateServiceSequenceQuery,
  useCalculateTariffScheduleServiceGroupsQuery,
  useCancelEditTariffScheduleServiceGroupListMutation,
  useCancelTariffScheduleServiceGroupMutation,
  useEditTariffScheduleServiceGroupListMutation,
  useEditTariffScheduleServiceGroupMutation,
  useGetContractServiceGroupsQuery,
  useGetServiceGroupSuggestedServicesQuery,
  useGetTariffScheduleServiceGroupQuery,
  useGetTariffScheduleServiceGroupsForRestrictionQuery,
  useKeepAliveTariffScheduleServiceGroupListMutation,
  useKeepAliveTariffScheduleServiceGroupMutation,
  useTariffScheduleServiceGroupsQuery,
  useUpdateTariffScheduleServiceGroupListMutation,
  useUpdateTariffScheduleServiceGroupMutation,
} from '@/graphql/generated';
import { OperationStatus } from '@/graphql/generated';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

export type TariffServiceGroups = TariffScheduleServiceGroupsQuery['tariffScheduleServiceGroups'];
export type TariffServiceGroupListItem = ArrayElementType<
  TariffScheduleServiceGroupsQuery['tariffScheduleServiceGroups']['serviceGroups']
>;
export type AddServiceGroupPayload = AddTariffScheduleServiceGroupMutation['addTariffScheduleServiceGroup'];
export type ContractServiceGroups = GetContractServiceGroupsQuery['contractServiceGroups'];
export type ServiceGroup = GetTariffScheduleServiceGroupQuery['tariffScheduleServiceGroup'];
export type ServiceGroupSuggested = GetServiceGroupSuggestedServicesQuery['serviceGroupSuggestedServices'];
export type SuggestedService = NonNullable<
  NonNullable<GetServiceGroupSuggestedServicesQuery['serviceGroupSuggestedServices']>[number]
>['services'][number];

const suggestedServices = ref(loadable.empty<ServiceGroupSuggested>());
const tariffServiceGroups = ref<TariffServiceGroups>();
const contractServiceGroups = ref<ContractServiceGroups>();

export const useServiceGroupsStore = () => {
  const { activeAccount } = useAccommodationObjectsStore();

  const { activeTariff } = useTariffsStore();

  const { integration } = useTravelLineStore();
  const { integration: reverseIntegration } = useReverseIntegrationStore();

  const getSuggestedServices = async () => {
    if (suggestedServices.value.loaded) {
      return;
    }

    const { request } = useGetServiceGroupSuggestedServicesQuery({});
    const result = await request();

    if (result?.data) {
      suggestedServices.value = loadable.loaded(result.data.serviceGroupSuggestedServices);
    }
  };

  const getTariffServiceGroups = async () => {
    if (!activeTariff.value) {
      return;
    }

    const { request } = useTariffScheduleServiceGroupsQuery({
      tariffScheduleCid: activeTariff.value.tariffScheduleCid,
    });
    const result = await request();

    if (result?.data) {
      tariffServiceGroups.value = result.data.tariffScheduleServiceGroups;
    }

    return result;
  };

  const getTariffScheduleServiceGroup = async (
    serviceGroupCid: string,
    hotelSetCid: string,
    tariffScheduleCid?: string,
  ) => {
    const { request } = useGetTariffScheduleServiceGroupQuery({
      serviceGroupCid: serviceGroupCid,
      hotelSetCid: hotelSetCid,
      tariffScheduleCid: tariffScheduleCid,
    });
    const result = await request();

    return result;
  };

  const createServiceGroup = async (payload: AddTariffScheduleServiceGroupInput) => {
    const { request } = useAddTariffScheduleServiceGroupMutation(payload);
    const result = await request();

    if (result?.data && tariffServiceGroups.value?.serviceGroups) {
      tariffServiceGroups.value = result!.data.addTariffScheduleServiceGroup.serviceGroups!;
    }

    return result;
  };

  const calculateServiceSequence = async (payload: {
    tariffScheduleCid?: string;
    hotelSetCid: string;
    serviceCids: string[];
  }) => {
    const { request } = useCalculateServiceSequenceQuery(payload);
    const result = await request();
    return result;
  };

  const getTariffScheduleServiceGroupsForRestriction = async (payload: {
    tariffScheduleCid: string;
    checkInOutRestrictionId: number;
  }) => {
    const { request } = useGetTariffScheduleServiceGroupsForRestrictionQuery(payload);
    const result = await request();
    return result;
  };

  const calculateTariffScheduleServiceGroups = async (payload: {
    tariffScheduleCid: string;
    checkInOutSettings: {
      checkInTime: string;
      checkOutTime: string;
      nextDayCheckout: boolean;
    };
  }) => {
    const { request } = useCalculateTariffScheduleServiceGroupsQuery(payload);
    const result = await request();
    return result;
  };

  const editTariffScheduleServiceGroupStartAction = async (id: string) => {
    const { request } = useEditTariffScheduleServiceGroupMutation({
      serviceGroupCid: id,
      tariffScheduleCid: activeTariff?.value?.tariffScheduleCid,
      hotelSetCid:
        activeTariff?.value?.hotelSetCid ??
        integration.value?.master?.hotelSetCid ??
        reverseIntegration.value!.master!.hotelSetCid,
    });
    const result = await request();

    return result;
  };

  const editTariffScheduleServiceGroupKeepAliveAction = async (id: string) => {
    const { request } = useKeepAliveTariffScheduleServiceGroupMutation({
      serviceGroupCid: id,
      hotelSetCid: activeTariff!.value!.hotelSetCid,
    });
    const result = await request();

    return result;
  };

  const editTariffScheduleServiceGroupCancelAction = async (id: string) => {
    const { request } = useCancelTariffScheduleServiceGroupMutation({
      serviceGroupCid: id,
      hotelSetCid:
        activeTariff?.value?.hotelSetCid ??
        integration.value?.master?.hotelSetCid ??
        reverseIntegration.value!.master!.hotelSetCid,
    });

    const result = await request();

    return result;
  };

  const editTariffScheduleServiceGroupUpdateAction = async (payload: UpdateTariffScheduleServiceGroupInput) => {
    const { request } = useUpdateTariffScheduleServiceGroupMutation({
      tariffScheduleCid: payload.tariffScheduleCid,
      serviceGroupCid: payload.serviceGroupCid,
      serviceGroup: payload.serviceGroup,
      hotelSetCid: payload.hotelSetCid,
    });
    const result = await request();

    if (result?.data && tariffServiceGroups.value?.serviceGroups) {
      const updatedServiceGroup = result.data.updateTariffScheduleServiceGroup.serviceGroup!;

      const existingServiceGroup = tariffServiceGroups.value.serviceGroups.find(
        (x) => x.serviceGroupCid === updatedServiceGroup.cid,
      );

      if (existingServiceGroup) {
        existingServiceGroup.name = updatedServiceGroup.name;
        existingServiceGroup.services = updatedServiceGroup.services;
        existingServiceGroup.description = updatedServiceGroup.description;
      }
    }

    return result;
  };

  const {
    isResourceEdit: isEditTariffScheduleServiceGroupEdit,
    isEditResourceStartLoading: isEditTariffScheduleServiceGroupStartLoading,
    isEditResourceUpdateLoading: isEditTariffScheduleServiceGroupUpdateLoading,
    editResourceStart: editTariffScheduleServiceGroupStart,
    editResourceCancel: editTariffScheduleServiceGroupCancel,
    editResourceUpdate: editTariffScheduleServiceGroupUpdate,
  } = useLockResource(
    'TariffScheduleServiceGroups',
    editTariffScheduleServiceGroupStartAction,
    editTariffScheduleServiceGroupKeepAliveAction,
    editTariffScheduleServiceGroupCancelAction,
    editTariffScheduleServiceGroupUpdateAction,
  );

  const getContractServiceGroups = async () => {
    if (!activeAccount.value) {
      return;
    }

    const { request } = useGetContractServiceGroupsQuery({
      contractNumber: activeAccount.value.contractNumber,
    });
    const result = await request();

    if (result?.data) {
      contractServiceGroups.value = result.data.contractServiceGroups;
    }

    return result;
  };

  const editTariffScheduleServiceGroupListStartAction = async (id: string) => {
    const { request } = useEditTariffScheduleServiceGroupListMutation({
      tariffScheduleCid: id,
    });
    const result = await request();

    if (result?.data) {
      tariffServiceGroups.value = result.data.editTariffScheduleServiceGroupList.serviceGroups!;
    }

    return result;
  };

  const editTariffScheduleServiceGroupKeepAliveListAction = async (id: string) => {
    const { request } = useKeepAliveTariffScheduleServiceGroupListMutation({
      tariffScheduleCid: id,
    });
    const result = await request();

    return result;
  };

  const editTariffScheduleServiceGroupCancelListAction = async (id: string) => {
    const { request } = useCancelEditTariffScheduleServiceGroupListMutation({
      tariffScheduleCid: id,
    });
    const result = await request();

    return result;
  };

  const editTariffScheduleServiceGroupUpdateListAction = async (payload: UpdateTariffScheduleServiceGroupListInput) => {
    const { request } = useUpdateTariffScheduleServiceGroupListMutation(payload);
    const result = await request();

    if (result?.data) {
      tariffServiceGroups.value = result.data.updateTariffScheduleServiceGroupList.serviceGroups!;
    }

    return result;
  };

  const {
    isEditResourceStartLoading: isEditTariffScheduleServiceGroupListStartLoading,
    isEditResourceUpdateLoading: isEditTariffScheduleServiceGroupListUpdateLoading,
    editResourceStart: editTariffScheduleServiceGroupListStart,
    editResourceCancel: editTariffScheduleServiceGroupListCancel,
    editResourceUpdate: editTariffScheduleServiceGroupListUpdate,
  } = useLockResource(
    'TariffScheduleServiceGroupList',
    editTariffScheduleServiceGroupListStartAction,
    editTariffScheduleServiceGroupKeepAliveListAction,
    editTariffScheduleServiceGroupCancelListAction,
    editTariffScheduleServiceGroupUpdateListAction,
  );

  const setDisabledOperations = () => {
    if (tariffServiceGroups.value) {
      tariffServiceGroups.value.operations.edit = OperationStatus.Disabled;
    }
  };

  return {
    createServiceGroup,

    suggestedServices,
    getSuggestedServices,
    setDisabledOperations,

    getTariffServiceGroups,
    tariffServiceGroups,

    getContractServiceGroups,
    contractServiceGroups,

    getTariffScheduleServiceGroup,

    calculateServiceSequence,
    getTariffScheduleServiceGroupsForRestriction,
    calculateTariffScheduleServiceGroups,

    isEditTariffScheduleServiceGroupEdit,
    isEditTariffScheduleServiceGroupStartLoading,
    isEditTariffScheduleServiceGroupUpdateLoading,
    editTariffScheduleServiceGroupStart,
    editTariffScheduleServiceGroupCancel,
    editTariffScheduleServiceGroupUpdate,

    isEditTariffScheduleServiceGroupListStartLoading,
    isEditTariffScheduleServiceGroupListUpdateLoading,
    editTariffScheduleServiceGroupListStart,
    editTariffScheduleServiceGroupListCancel,
    editTariffScheduleServiceGroupListUpdate,
  };
};
